import { Box, HStack, Text } from "@chakra-ui/react";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useFormikContext } from "formik";
import { Fragment, useCallback, useMemo } from "react";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { FormikSwitchField } from "~/src/components/common/forms/FormikSwitchField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { customStyles } from "~/src/components/common/Select/styles";

import { FunnelImportFormValues, defaultPageValues } from "../validationSchema";

export type Props = {
  funnelIndex: number;
  onDeleteFunnel: (index: number) => void;
};

/** Same widths as popup CSV import sticky columns (`FunnelImportForm`). */
const STICKY_DELETE_W = "44px";
const STICKY_ROW_W = "52px";

const ACTION_OPTIONS = [
  { label: "（未選択）", value: "" },
  { label: "登録", value: "CREATE" },
  { label: "編集", value: "UPDATE" },
];

const selectWithMenuPortalProps = {
  menuPortalTarget: document.body,
  menuPosition: "fixed" as const,
  styles: {
    ...customStyles,
    menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  },
};

type PageCellsProps = {
  fieldPrefix: string;
  showHasForm?: boolean;
};

function PageCells({ fieldPrefix, showHasForm = false }: PageCellsProps) {
  return (
    <>
      <Box as="td" minW="200px" p="4px 8px" verticalAlign="top">
        <FormikTextField
          mode="fill"
          type="text"
          name={`${fieldPrefix}.url`}
          placeholder="https://example.com"
        />
      </Box>
      <Box as="td" minW="200px" p="4px 8px" verticalAlign="top">
        <FormikTextField
          mode="fill"
          type="text"
          name={`${fieldPrefix}.mobileUrl`}
          placeholder="https://example.com"
        />
      </Box>
      <Box as="td" minW="200px" p="4px 8px" verticalAlign="top">
        <FormikTextField
          mode="fill"
          type="text"
          name={`${fieldPrefix}.tabletUrl`}
          placeholder="https://example.com"
        />
      </Box>
      <Box as="td" minW="140px" p="4px 8px" verticalAlign="top">
        <FormikSelectField
          name={`${fieldPrefix}.queryMatchType`}
          options={GraphQLEnums.QueryMatch}
          {...selectWithMenuPortalProps}
        />
      </Box>
      <Box as="td" minW="140px" p="4px 8px" verticalAlign="top">
        <FormikSelectField
          name={`${fieldPrefix}.urlMatchType`}
          options={GraphQLEnums.UrlMatch}
          {...selectWithMenuPortalProps}
        />
      </Box>
      {showHasForm && (
        <Box
          as="td"
          minW="64px"
          p="4px 8px"
          verticalAlign="middle"
          textAlign="center"
        >
          <FormikSwitchField name={`${fieldPrefix}.landingUrlFormIncluded`} />
        </Box>
      )}
      <Box
        as="td"
        minW="64px"
        p="4px 8px"
        verticalAlign="middle"
        textAlign="center"
      >
        <FormikSwitchField name={`${fieldPrefix}.isEnableWhiteout`} />
      </Box>
      <Box
        as="td"
        minW="64px"
        p="4px 8px"
        verticalAlign="middle"
        textAlign="center"
      >
        <FormikSwitchField name={`${fieldPrefix}.isSpa`} />
      </Box>
    </>
  );
}

// Landing page has 8 cols; other pages have 7 cols (no landing form switch)
function EmptyPageCells({ showHasForm = false }: { showHasForm?: boolean }) {
  const colCount = showHasForm ? 8 : 7;
  return (
    <>
      {Array.from({ length: colCount }).map((_, i) => (
        <Box key={i} as="td" p="4px 8px" />
      ))}
    </>
  );
}

export function FunnelImportGroup({ funnelIndex, onDeleteFunnel }: Props) {
  const { values, setFieldValue } = useFormikContext<FunnelImportFormValues>();
  const funnel = values.funnels[funnelIndex];

  const formPages = useMemo(() => funnel?.formPages ?? [], [funnel?.formPages]);
  // Always render at least 1 row (for funnel-level info + form page slot)
  const rowCount = Math.max(formPages.length, 1);

  const handleDeleteFunnel = useCallback(() => {
    onDeleteFunnel(funnelIndex);
  }, [funnelIndex, onDeleteFunnel]);

  const handleAddFormPage = useCallback(() => {
    setFieldValue(`funnels[${funnelIndex}].formPages`, [
      ...formPages,
      { ...defaultPageValues },
    ]);
  }, [funnelIndex, formPages, setFieldValue]);

  const makeRemoveFormPage = useCallback(
    (pageIndex: number) => () => {
      setFieldValue(
        `funnels[${funnelIndex}].formPages`,
        formPages.filter((_, i) => i !== pageIndex)
      );
    },
    [funnelIndex, formPages, setFieldValue]
  );

  const isUpdateAction = funnel?.action === "UPDATE";
  const rowBg = funnelIndex % 2 === 0 ? "white" : "#F7FAFC";

  return (
    <>
      {Array.from({ length: rowCount }).map((_, pageIndex) => {
        const isFirstRow = pageIndex === 0;
        const removeFormPage = makeRemoveFormPage(pageIndex);

        return (
          <Fragment key={pageIndex}>
            <Box as="tr" borderBottom="1px solid #E2E8F0" bg={rowBg}>
              {/* Delete funnel — first row only */}
              <Box
                as="td"
                w={STICKY_DELETE_W}
                minW={STICKY_DELETE_W}
                p="4px 8px"
                verticalAlign="top"
                textAlign="center"
                position="sticky"
                left={0}
                bg={rowBg}
                zIndex={2}
              >
                {isFirstRow && (
                  <Box
                    as="button"
                    type="button"
                    onClick={handleDeleteFunnel}
                    color="#E53E3E"
                    display="inline-flex"
                    alignItems="center"
                    _hover={{ color: "#C53030" }}
                    title="ファネルを削除"
                  >
                    <DeleteIcon fontSize="small" />
                  </Box>
                )}
              </Box>

              {/* Row index — first row only (matches popup import table) */}
              <Box
                as="td"
                w={STICKY_ROW_W}
                minW={STICKY_ROW_W}
                p="4px 8px"
                verticalAlign="top"
                position="sticky"
                left={STICKY_DELETE_W}
                bg={rowBg}
                zIndex={2}
              >
                {isFirstRow && (
                  <Text
                    fontSize="11px"
                    fontWeight="bold"
                    color="#4A5568"
                    whiteSpace="nowrap"
                  >
                    {funnelIndex + 1}
                  </Text>
                )}
              </Box>

              {/* Funnel-level columns — first row only */}
              <Box as="td" minW="120px" p="4px 8px" verticalAlign="top">
                {isFirstRow && (
                  <FormikSelectField
                    name={`funnels[${funnelIndex}].action`}
                    options={ACTION_OPTIONS}
                    {...selectWithMenuPortalProps}
                  />
                )}
              </Box>
              <Box as="td" minW="120px" p="4px 8px" verticalAlign="top">
                {isFirstRow && isUpdateAction && (
                  <FormikTextField
                    mode="fill"
                    type="text"
                    name={`funnels[${funnelIndex}].funnelId`}
                    placeholder="ファネルID"
                  />
                )}
              </Box>
              <Box as="td" minW="160px" p="4px 8px" verticalAlign="top">
                {isFirstRow && (
                  <FormikTextField
                    mode="fill"
                    type="text"
                    name={`funnels[${funnelIndex}].name`}
                    placeholder="ファネル名"
                  />
                )}
              </Box>

              {/* Landing page — first row only */}
              {isFirstRow ? (
                <PageCells
                  fieldPrefix={`funnels[${funnelIndex}].landingPage`}
                  showHasForm
                />
              ) : (
                <EmptyPageCells showHasForm />
              )}

              {/* Form page — always show editable fields (bound to formPages[pageIndex]) */}
              <PageCells
                fieldPrefix={`funnels[${funnelIndex}].formPages[${pageIndex}]`}
              />

              {/* Form page add/remove actions — placed at end of form page columns */}
              <Box
                as="td"
                minW="72px"
                p="4px 8px"
                verticalAlign="middle"
                textAlign="center"
                whiteSpace="nowrap"
              >
                <HStack spacing="8px" justifyContent="flex-start">
                  <Box
                    as="button"
                    type="button"
                    onClick={handleAddFormPage}
                    color="#38A169"
                    display="inline-flex"
                    alignItems="center"
                    lineHeight="1"
                    _hover={{ color: "#2F855A" }}
                    _active={{ color: "#276749" }}
                    title="フォームページを追加"
                    aria-label="フォームページを追加"
                  >
                    <AddIcon fontSize="small" />
                  </Box>
                  {!isFirstRow && (
                    <Box
                      as="button"
                      type="button"
                      onClick={removeFormPage}
                      color="#E53E3E"
                      display="inline-flex"
                      alignItems="center"
                      lineHeight="1"
                      _hover={{ color: "#C53030" }}
                      _active={{ color: "#9B2C2C" }}
                      title="このフォームページをクリア"
                      aria-label="このフォームページをクリア"
                    >
                      <ClearIcon fontSize="small" />
                    </Box>
                  )}
                </HStack>
              </Box>

              {/* Confirm page — first row only */}
              {isFirstRow ? (
                <PageCells
                  fieldPrefix={`funnels[${funnelIndex}].confirmPage`}
                />
              ) : (
                <EmptyPageCells />
              )}

              {/* Thanks page — first row only */}
              {isFirstRow ? (
                <PageCells fieldPrefix={`funnels[${funnelIndex}].thanksPage`} />
              ) : (
                <EmptyPageCells />
              )}

              {/* isExperiment — first row only */}
              <Box
                as="td"
                minW="80px"
                p="4px 8px"
                verticalAlign="middle"
                textAlign="center"
              >
                {isFirstRow && (
                  <FormikSwitchField
                    name={`funnels[${funnelIndex}].isExperiment`}
                  />
                )}
              </Box>
            </Box>
          </Fragment>
        );
      })}
    </>
  );
}
